'use client'

import { useState, useEffect } from 'react'
import { useTranslation } from '@/i18n/client'

const COOKIE_CONSENT_KEY = 'fgl_cookie_consent'

export interface CookieConsent {
  necessary: boolean
  analytics: boolean
  /** R102.6 (Stephan-Wunsch 2026-05-31): externe Karten-Tiles (OSM, OpenTopoMap,
   *  EOX, flagcdn). Wenn abgelehnt: Karten rendern ohne Hintergrund-Tiles,
   *  nur Flugspur auf theme-farbigem Hintergrund. */
  externalMapTiles: boolean
  timestamp: number
}

/**
 * DSGVO Cookie-Banner
 * Speichert Einwilligung in localStorage.
 *
 * R102.6: dritte Kategorie „Externe Karten-Tiles" ergänzt. Im Datenschutz § 14a
 * dokumentiert. Karten-Komponenten (Cesium, MapLibre, Leaflet) prüfen vor dem
 * Tile-Layer-Mount `getCookieConsent()?.externalMapTiles` und fallen sonst auf
 * den theme-farbigen Hintergrund zurück.
 */
export function CookieBanner() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
      if (!stored) {
        setVisible(true)
        return
      }
      // R102.6: bestehende Consents ohne externalMapTiles-Feld werden migriert
      // auf Default „true" (rückwärtskompatibel — bisheriger Standard war: Karten
      // funktionierten). Wer aktiv ablehnen will, klickt nochmal über
      // /datenschutz.
      try {
        const parsed = JSON.parse(stored) as Partial<CookieConsent>
        if (typeof parsed.externalMapTiles === 'undefined') {
          const migrated: CookieConsent = {
            necessary: true,
            analytics: parsed.analytics ?? false,
            externalMapTiles: true,
            timestamp: parsed.timestamp ?? Date.now(),
          }
          localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(migrated))
        }
      } catch {}
    } catch {
      setVisible(true)
    }
  }, [])

  const saveConsent = (consent: CookieConsent) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent))
      // R102.6: Karten-Komponenten lauschen auf dieses Event, um Layer
      // dynamisch nachzuladen oder abzuräumen ohne Page-Reload.
      window.dispatchEvent(new CustomEvent('fgl:cookie-consent-changed', { detail: consent }))
    } catch {}
    setVisible(false)
  }

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, externalMapTiles: true, timestamp: Date.now() })
  }

  const acceptNecessary = () => {
    saveConsent({ necessary: true, analytics: false, externalMapTiles: false, timestamp: Date.now() })
  }

  const [granular, setGranular] = useState<{ analytics: boolean; externalMapTiles: boolean }>({
    analytics: false,
    externalMapTiles: true,
  })

  const acceptGranular = () => {
    saveConsent({
      necessary: true,
      analytics: granular.analytics,
      externalMapTiles: granular.externalMapTiles,
      timestamp: Date.now(),
    })
  }

  if (!visible) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4" style={{ background: 'var(--modal-overlay, rgba(0,0,0,0.5))', backdropFilter: 'blur(4px)' }}>
      <div className="max-w-3xl mx-auto rounded-[16px] p-5 sm:p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 -4px 20px rgba(0,0,0,0.3)' }}>

        <h3 className="text-base font-bold mb-2" style={{ color: 'var(--text)' }}>
          {t('cookies.title')}
        </h3>

        <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
          {t('cookies.description')}
        </p>

        {showDetails && (
          <div className="mb-4 space-y-3 text-sm" style={{ color: 'var(--muted)' }}>
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
              <div>
                <strong style={{ color: 'var(--text)' }}>{t('cookies.necessary')}</strong>
                <p className="text-xs mt-1">{t('cookies.necessaryDesc')}</p>
              </div>
              <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--accent)', color: '#000' }}>{t('cookies.alwaysActive')}</span>
            </div>

            <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer" style={{ background: 'var(--bg-secondary)' }}>
              <div className="flex-1 mr-3">
                <strong style={{ color: 'var(--text)' }}>{t('cookies.analytics')}</strong>
                <p className="text-xs mt-1">{t('cookies.analyticsDesc')}</p>
              </div>
              <input
                type="checkbox"
                checked={granular.analytics}
                onChange={(e) => setGranular(g => ({ ...g, analytics: e.target.checked }))}
                className="h-5 w-5"
                style={{ accentColor: 'var(--accent)' }}
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg cursor-pointer" style={{ background: 'var(--bg-secondary)' }}>
              <div className="flex-1 mr-3">
                <strong style={{ color: 'var(--text)' }}>{t('cookies.externalMapTiles')}</strong>
                <p className="text-xs mt-1">{t('cookies.externalMapTilesDesc')}</p>
              </div>
              <input
                type="checkbox"
                checked={granular.externalMapTiles}
                onChange={(e) => setGranular(g => ({ ...g, externalMapTiles: e.target.checked }))}
                className="h-5 w-5"
                style={{ accentColor: 'var(--accent)' }}
              />
            </label>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={acceptAll} className="btn-primary flex-1 py-2.5 text-sm font-bold">
            {t('cookies.acceptAll')}
          </button>
          {showDetails ? (
            <button onClick={acceptGranular} className="btn-secondary flex-1 py-2.5 text-sm font-bold">
              {t('cookies.acceptSelection')}
            </button>
          ) : (
            <button onClick={acceptNecessary} className="btn-secondary flex-1 py-2.5 text-sm font-bold">
              {t('cookies.necessaryOnly')}
            </button>
          )}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="py-2.5 text-sm underline"
            style={{ color: 'var(--muted)' }}
          >
            {showDetails ? t('cookies.hideDetails') : t('cookies.showDetails')}
          </button>
        </div>

        <p className="text-center text-xs mt-3" style={{ color: 'var(--muted)' }}>
          <a href="/datenschutz" className="underline">{t('cookies.privacyLink')}</a>
        </p>
      </div>
    </div>
  )
}

/** Hilfsfunktion: Cookie-Consent abfragen */
export function getCookieConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<CookieConsent>
    // R102.6: rückwärtskompatible Default für externalMapTiles
    return {
      necessary: true,
      analytics: parsed.analytics ?? false,
      externalMapTiles: parsed.externalMapTiles ?? true,
      timestamp: parsed.timestamp ?? 0,
    }
  } catch {
    return null
  }
}
